import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { nextSearchActiveIndex, pickHighlightedOnEnter } from '../../utils/menuSearchNav';

const EstimateDropdown = ({ compact = false, shrink = false, fullWidth = false, onSelectDepartment, onSelectEstimate, selectedEstimate }) => {
  const { instance, accounts } = useMsal();
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  // The dropdown panel is rendered through a portal, so it is NOT a DOM descendant of
  // dropdownRef. Without tracking it separately, the click-outside handler treated a
  // tap inside the panel as 'outside' and closed it on touchstart - before the item's
  // click could fire - so on touch devices a selection never registered at all.
  const panelRef = useRef(null);
  const timeoutRef = useRef(null);
  
  const [isOpen, setIsOpen] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasFetched, setHasFetched] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [highlightArmed, setHighlightArmed] = useState(false);
  const listRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setSearchTerm('');
    }, 150);
  };

  const handleClick = () => {
    if (compact) {
      setIsOpen(!isOpen);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!compact || !isOpen) return;
    
    const handleClickOutside = (e) => {
      const inTrigger = dropdownRef.current && dropdownRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [compact, isOpen]);

  useEffect(() => {
    if (isOpen && !hasFetched && accounts[0]) {
      fetchEstimates();
    }
  }, [isOpen, hasFetched, accounts]);

  const fetchEstimates = async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/estimates'
      );
      if (response.data?.success && response.data.estimates) {
        setEstimates(response.data.estimates);
        setHasFetched(true);
      }
    } catch (error) {
      console.error('Error fetching estimates:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEstimates = useMemo(() => {
    if (!searchTerm.trim()) return estimates;
    const search = searchTerm.toLowerCase();
    return estimates.filter(e =>
      (e.estimate_number || '').toLowerCase().includes(search) ||
      (e.estimate_name || '').toLowerCase().includes(search) ||
      (e.division_name || '').toLowerCase().includes(search)
    );
  }, [estimates, searchTerm]);

  useEffect(() => {
    setHighlightIndex(0);
    setHighlightArmed(false);
  }, [searchTerm, isOpen]);

  const showHighlight = Boolean(searchTerm.trim()) || highlightArmed;

  useEffect(() => {
    if (!showHighlight) return;
    const list = listRef.current;
    const el = list?.querySelector(`[data-idx="${highlightIndex}"]`);
    if (!list || !el) return;
    const listRect = list.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    if (elRect.top < listRect.top) list.scrollTop -= listRect.top - elRect.top;
    else if (elRect.bottom > listRect.bottom) list.scrollTop += elRect.bottom - listRect.bottom;
  }, [highlightIndex, showHighlight, filteredEstimates.length]);

  const handleSelectEstimate = (estimate) => {
    setIsOpen(false);
    setSearchTerm('');
    if (onSelectDepartment) {
      onSelectDepartment('estimation');
    }
    if (onSelectEstimate) {
      onSelectEstimate(estimate);
    }
  };

  const displayLabel = selectedEstimate
    ? selectedEstimate.estimate_number || 'Estimate'
    : 'Select Estimate';

  const compactLabel = selectedEstimate?.estimate_number || 'Est.';

  return (
    <div
      ref={dropdownRef}
      onMouseEnter={!compact ? handleMouseEnter : undefined}
      onMouseLeave={!compact ? handleMouseLeave : undefined}
      style={fullWidth
        ? { position: 'relative', display: 'block', width: '100%', minWidth: 0 }
        : { position: 'relative', display: 'inline-block', flexShrink: shrink ? 1 : 0, overflow: 'hidden', maxWidth: compact ? '110px' : '260px', minWidth: compact ? 48 : 0 }}
    >
      <button
        ref={triggerRef}
        onClick={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? '4px' : '6px',
          height: fullWidth ? '40px' : (compact ? '36px' : '44px'),
          width: fullWidth ? '100%' : undefined,
          justifyContent: fullWidth ? 'space-between' : undefined,
          padding: fullWidth ? '0 0.75rem' : (compact ? '0 0.5rem' : '0 1.25rem'),
          background: 'transparent',
          borderRadius: '8px',
          cursor: 'pointer',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.2s ease',
          maxWidth: '100%',
          overflow: 'hidden',
          // The 160px floor keeps the button a comfortable size normally, but it must be
          // released while shrinking or the button cannot follow its wrapper down and
          // ends up drawn over the dropdown beside it.
          minWidth: compact ? undefined : (shrink ? 0 : '160px'),
          fontSize: fullWidth ? '0.85rem' : (compact ? '0.75rem' : '0.9rem'),
          fontWeight: '500',
          letterSpacing: '0.2px',
          color: 'white',
          boxSizing: 'border-box',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(220, 53, 69, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%)';
          e.currentTarget.style.borderColor = '#dc3545';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        title={displayLabel}
      >
        {(compact || fullWidth) && <span style={{ fontSize: '14px', flexShrink: 0 }}>💰</span>}
        
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}>
          {compact && !fullWidth ? compactLabel : displayLabel}
        </span>
        
        <span style={{
          fontSize: '8px',
          marginLeft: compact ? '2px' : '4px',
          opacity: 0.8,
          transition: 'transform 0.2s ease',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>
          ▼
        </span>
      </button>

      {isOpen && createPortal(
        <div
          ref={panelRef}
          onMouseEnter={!compact ? handleMouseEnter : undefined}
          onMouseLeave={!compact ? handleMouseLeave : undefined}
          style={{
            position: 'fixed',
            top: triggerRef.current 
              ? `${triggerRef.current.getBoundingClientRect().bottom + 8}px` 
              : '60px',
            left: compact 
              ? '10px'
              : (triggerRef.current 
                ? `${Math.min(triggerRef.current.getBoundingClientRect().left, window.innerWidth - 350)}px`
                : '100px'),
            right: compact ? '10px' : 'auto',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            zIndex: 9999,
            width: compact ? 'auto' : '340px',
            maxWidth: compact ? 'calc(100vw - 20px)' : '340px',
            maxHeight: compact ? '60vh' : '400px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            animation: 'dropdownSlideIn 0.15s ease-out',
          }}
        >
          {/* Search Header */}
          <div style={{
            padding: '12px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
          }}>
            <div style={{
              fontSize: '11px',
              color: '#6b7280',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px',
            }}>
              Select Estimate
            </div>
            <input
              type="text"
              placeholder="Search by number, project, or client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsOpen(false);
                  setSearchTerm('');
                } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  const direction = e.key === 'ArrowDown' ? 'down' : 'up';
                  const current = showHighlight ? highlightIndex : -1;
                  const next = nextSearchActiveIndex(current, direction, filteredEstimates.length);
                  if (next >= 0) {
                    setHighlightIndex(next);
                    setHighlightArmed(true);
                  }
                } else if (e.key === 'Enter') {
                  const pick = pickHighlightedOnEnter(filteredEstimates, highlightIndex, {
                    query: searchTerm,
                    armed: highlightArmed,
                  });
                  if (pick) {
                    e.preventDefault();
                    handleSelectEstimate(pick);
                  }
                }
              }}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s ease',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#dc3545'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* Estimates List */}
          <div
            ref={listRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              maxHeight: '320px',
            }}
          >
            {loading ? (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                color: '#6b7280',
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid #f3f4f6',
                  borderTop: '3px solid #dc3545',
                  borderRadius: '50%',
                  margin: '0 auto 8px',
                  animation: 'spin 0.8s linear infinite',
                }} />
                Loading estimates...
              </div>
            ) : filteredEstimates.length === 0 ? (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                color: '#6b7280',
              }}>
                {searchTerm ? 'No estimates match your search' : 'No estimates available'}
              </div>
            ) : (
              filteredEstimates.map((estimate, idx) => {
                const isSelected = selectedEstimate && String(selectedEstimate.id) === String(estimate.id);
                const isHighlighted = showHighlight && idx === highlightIndex;
                return (
                  <div
                    key={estimate.id}
                    data-idx={idx}
                    onClick={() => handleSelectEstimate(estimate)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f3f4f6',
                      backgroundColor: isHighlighted ? '#fee2e2' : isSelected ? '#fef2f2' : 'transparent',
                      boxShadow: isHighlighted ? 'inset 3px 0 0 #dc3545' : 'none',
                      transition: 'background-color 0.15s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                    onMouseEnter={() => {
                      setHighlightIndex(idx);
                      setHighlightArmed(true);
                    }}
                  >
                    <div style={{
                      minWidth: '80px',
                      height: '42px',
                      padding: '0 8px',
                      borderRadius: '8px',
                      backgroundColor: isSelected ? '#dc3545' : '#e5e7eb',
                      color: isSelected ? 'white' : '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: '700',
                      flexShrink: 0,
                      boxSizing: 'border-box',
                    }}>
                      {estimate.estimate_number || '—'}
                    </div>
                    
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#111827',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {estimate.estimate_name || 'Unnamed Estimate'}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginTop: '2px',
                      }}>
                        {estimate.division_name || '—'}
                        {estimate.awarded && (
                          <span style={{
                            marginLeft: '8px',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600',
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                          }}>
                            Awarded
                          </span>
                        )}
                      </div>
                    </div>

                    {isSelected && (
                      <div style={{
                        color: '#dc3545',
                        fontSize: '14px',
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Animation Keyframes */}
          <style>{`
            @keyframes dropdownSlideIn {
              from {
                opacity: 0;
                transform: translateY(-8px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>,
        document.body
      )}
    </div>
  );
};

export default EstimateDropdown;
